import type { PropsWithChildren } from 'react';
import { MoreIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { getContrastingTextColor } from '../../utils/colors';
import { getAvatarsToRender } from './utils';
import type { AvatarStackProps } from './types';

const OVERLAP_PERCENTAGE = '-10%';

interface AvatarHtmlProps extends BoxT {
  color?: string;
  radius?: string | undefined;
  fontSize?: string | undefined;
}

export const Avatar = ({
  color,
  radius = '40px',
  fontSize = '87.5',
  children,
  ...props
}: PropsWithChildren<AvatarHtmlProps>) => {
  const textColor = color ? getContrastingTextColor(color) : 'onPrimaryWeak';

  return (
    <Box
      bg={color ?? 'primaryWeak'}
      data-testid="avatar"
      borderRadius="full"
      width={radius}
      height={radius}
      alignItems="center"
      justifyContent="center"
      borderStyle="solid"
      borderColor="bg"
      {...props}
    >
      <Text
        color={textColor}
        fontSize={fontSize}
        fontWeight="semiBold"
        style={{ cursor: 'default' }}
      >
        {children}
      </Text>
    </Box>
  );
};

export interface AvatarStackHtmlProps extends AvatarStackProps {
  avatarRadius?: string;
  avatarFontSize?: string;
}

export const AvatarStackHtml = ({
  identities,
  maxNumberOfVisibleAvatars = 3,
  avatarRadius,
  avatarFontSize,
  totalVotes,
}: AvatarStackHtmlProps) => {
  if (!identities.length) {
    return null;
  }

  const { numberOfVisibleAvatars, numberOfExtraAvatars } = getAvatarsToRender(
    identities.length,
    maxNumberOfVisibleAvatars,
  );
  const avatarsToRender = identities.slice(0, numberOfVisibleAvatars);
  const hasReachedIdentityLimit = totalVotes > identities.length;

  return (
    <Box flexDirection="row" data-testid="avatar-stack">
      {avatarsToRender.map((identity, i) => {
        const marginLeft = i !== 0 ? OVERLAP_PERCENTAGE : 0;
        return (
          <Avatar
            key={identity.id}
            borderWidth="2px"
            style={{ marginLeft }}
            bg="neutralWeak"
            radius={avatarRadius}
            fontSize={avatarFontSize}
          >
            {identity.displayName.charAt(0).toUpperCase()}
          </Avatar>
        );
      })}
      {numberOfExtraAvatars > 0 && (
        <Avatar
          borderWidth="2px"
          style={{ marginLeft: OVERLAP_PERCENTAGE }}
          bg="neutralWeakest"
          radius={avatarRadius}
          fontSize={avatarFontSize}
        >
          {hasReachedIdentityLimit ? (
            <Box data-testid="more-icon">
              <MoreIcon />
            </Box>
          ) : (
            `+${numberOfExtraAvatars}`
          )}
        </Avatar>
      )}
    </Box>
  );
};
